import { PureComponent } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import Actions from '../../../../actions'
import Selectors from '../../../../selectors'
import { css, StyleSheet } from 'aphrodite/no-important'
import { colors } from '@lmc/common/constants'
import { getNominative } from '../../../../common/utils'
import {
  Pagination,
  Spinner,
  TableSortIcon,
} from '../../../../components/generic'
import LogsTableItem from './LogsTableItem'
import { gaEvent } from '../../../../common/services/analytics'
import { EmptyState, NoDataIcon } from '@lmc/ui-kit'

export class LogsTable extends PureComponent {
  handleChangePageNumber = (number) => {
    window.scrollTo(0, 0)
    this.props.setPageNumber(number)
    this.props.onChange()
    this.gaEvent({
      action: 'Change Page Number',
      label: number,
    })
  }

  handleChangePageSize = (size) => {
    this.props.setPageSize(size)
    this.props.onChange()
    this.gaEvent({
      action: 'Change Page Size',
      label: size,
    })
  }

  handleChangeSort = (sortType) => {
    const { onChange, setSortType, sort } = this.props
    const sortRegexp = new RegExp(sortType)
    if (sortRegexp.test(sort)) {
      const reverseSortRegexp = new RegExp(`-${sortType}`)
      const isReverseSorted = reverseSortRegexp.test(sort)
      if (!isReverseSorted) {
        setSortType(`-${sortType}`)
        onChange()
        return
      }
    }
    setSortType(sortType)
    onChange()
    this.gaEvent({
      action: 'Change Sort Type',
      label: sortType,
    })
  }

  gaEvent = ({ action, label }) =>
    gaEvent({
      category: 'Logs Dashboard',
      action,
      label,
    })

  renderPagination = (displayOptions = {}) => {
    const { count, page } = this.props
    return (
      <Pagination
        count={count}
        displayOptions={{
          ...displayOptions,
          lastPage: false,
        }}
        listId="logs"
        page={page}
        onChangePageNumber={this.handleChangePageNumber}
        onChangePageSize={this.handleChangePageSize}
      />
    )
  }

  renderTableHeader = () => {
    const { preferredPeopleNominative, sort } = this.props
    return (
      <div>
        <div className={css(classes.headersContainer)}>
          <div className={css(classes.timeLoggedHeaderContainer)}>
            <div
              className={css(classes.sortable)}
              id="sort-timeLogged"
              onClick={() => this.handleChangeSort('timeLogged')}
            >
              Time Logged
              <div className={css(classes.sortIcon)}>
                <TableSortIcon
                  ascending={sort === '-timeLogged'}
                  descending={sort === 'timeLogged'}
                />
              </div>
            </div>
          </div>
          <div className={css(classes.residentHeaderContainer)}>
            {getNominative(preferredPeopleNominative, 'Resident')}
          </div>
          <div className={css(classes.logHeaderContainer)}>Log</div>
        </div>
        <div className={css(classes.divider)} />
      </div>
    )
  }

  render() {
    const { loading, logs, hasAccess } = this.props

    const hasRolePermission = hasAccess(
      ['carehome-admin', 'senior-carer'],
      [
        {
          i: 'view',
          this: 'Log',
        },
      ]
    )

    if (!logs) return <Spinner />
    if (!logs.length)
      return (
        <EmptyState
          title="Shoot... No data to show you"
          description={NO_LOGS_HEADING}
          icon={<NoDataIcon />}
        />
      )
    return (
      <div className={css(classes.wrapper)}>
        <div className={css(classes.paginationContainer)}>
          {this.renderPagination({ count: true, pageSize: true })}
        </div>
        <div className={css(classes.tableContainer)}>
          {this.renderTableHeader()}
          {loading ? (
            <Spinner className={css(classes.tableSpinner)} />
          ) : (
            logs.map((log) => (
              <LogsTableItem
                key={log.id}
                log={log}
                residentStyle={styles.resident}
                linkEnabled={hasRolePermission}
              />
            ))
          )}
        </div>
        <div className={css(classes.paginationContainer)}>
          {this.renderPagination({ pageNumbers: true, pageSize: true })}
        </div>
      </div>
    )
  }
}

const NO_LOGS_HEADING = 'No logs found in date range'

const styles = {
  resident: {
    width: '25%',
  },
}

const classes = StyleSheet.create({
  wrapper: {
    padding: 24,
    backgroundColor: colors.white,
    borderRadius: 15,
    overflowX: 'auto',
  },
  divider: {
    background: colors.grey,
    height: 3,
    marginTop: 10,
  },
  headersContainer: {
    alignItems: 'center',
    display: 'flex',
    fontSize: 16,
    fontWeight: 600,
  },
  logHeaderContainer: {
    display: 'flex',
    width: '55%',
  },
  residentHeaderContainer: {
    display: 'flex',
    marginRight: 12,
    width: '25%',
  },
  sortable: {
    alignItems: 'center',
    cursor: 'pointer',
    display: 'flex',
  },
  sortIcon: {
    position: 'relative',
    bottom: 5,
  },
  tableContainer: {
    minWidth: 700,
    overflowX: 'auto',
    padding: '24px 0px',
  },
  tableSpinner: {
    paddingTop: 50,
    paddingBottom: 500,
  },
  timeLoggedHeaderContainer: {
    display: 'flex',
    marginRight: 12,
    width: '20%',
  },
})

LogsTable.propTypes = {
  count: PropTypes.number,
  logs: PropTypes.array,
  onChange: PropTypes.func.isRequired,
  page: PropTypes.shape({
    size: PropTypes.number.isRequired,
    number: PropTypes.number.isRequired,
  }).isRequired,
  preferredPeopleNominative: PropTypes.string,
  role: PropTypes.string.isRequired,
  setPageNumber: PropTypes.func.isRequired,
  setPageSize: PropTypes.func.isRequired,
  setSortType: PropTypes.func.isRequired,
  sort: PropTypes.string.isRequired,
}

const mapStateToProps = (state) => ({
  count: state.logs.count,
  loading: state.global.loading,
  logs: Selectors.populateLogs(state),
  page: state.logs.page,
  role: state.global.user.role,
  preferredPeopleNominative: state.global.home.preferredPeopleNominative,
  sort: state.global.sortTypes.logs,
  hasAccess: (roles, abilities) =>
    Selectors.checkRoleAccess(state, { roles, abilities }),
})

const mapDispatchToProps = (dispatch) => ({
  setPageNumber: (number) => dispatch(Actions.setLogsPageNumber(number)),
  setPageSize: (size) => dispatch(Actions.setLogsPageSize(size)),
  setSortType: (sortType) =>
    dispatch(
      Actions.setSortType({
        key: 'logs',
        sortType,
      })
    ),
})

export default connect(mapStateToProps, mapDispatchToProps)(LogsTable)
